import { Fragment, useEffect, useImperativeHandle, useLayoutEffect, useMemo, useRef, useState, type ClipboardEvent, type DragEvent, type KeyboardEvent } from "react";
import clsx from "clsx";
import { BellOff, Bell, CornerUpLeft, Download, FileText, Maximize2, Minus, MoreHorizontal, Paperclip, Pencil, SendHorizontal, Trash2, UserPlus, Users, X, LogOut, Type } from "lucide-react";
import { Avatar, Button, Dialog, IconButton, Menu, MenuContent, MenuItem, MenuSeparator, MenuTrigger } from "@dfws/ui";
import { CHAT_FILE_MAX_BYTES, type ChatChannel, type ChatFile } from "@dfws/contracts";
import { socketSend } from "../../lib/realtime";
import { ApiFailure } from "../../lib/api";
import {
  deleteMessage, editMessage, fileUrl, flatten, leaveChannel, markRead, markVisible, nameOf, presenceOf, removeFailed, updateChannel, uploadFile,
  useChannel, useChatPeople, useMessages, useSendMessage, useTyping, type UiMessage,
} from "./chatData";
import { buildRows, formatSize, linkify, timeLabel, typingText } from "./format";
import { MembersDialog, RenameDialog } from "./GroupDialogs";
import s from "./Chat.module.css";

export interface ConversationProps {
  channelId: string;
  me: string;
  variant: "dock" | "full";
  minimized?: boolean;
  onMinimize?: () => void;
  onClose?: () => void;
  onExpand?: () => void;
}

const PRESENCE_TEXT = { online: "Online", away: "Away", offline: "Offline" } as const;

export function Conversation({ channelId, me, variant, minimized, onMinimize, onClose, onExpand }: ConversationProps) {
  const channel = useChannel(channelId);
  const people = useChatPeople();
  const messages = useMessages(channelId);
  const list = useMemo(() => flatten(messages.data), [messages.data]);
  const typing = useTyping(channelId);
  const [replyTo, setReplyTo] = useState<UiMessage | null>(null);
  const [editing, setEditing] = useState<UiMessage | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<UiMessage | null>(null);
  const [dialog, setDialog] = useState<"rename" | "members" | null>(null);
  const [dragging, setDragging] = useState(false);
  const composer = useRef<ComposerHandle>(null);

  // On screen = mounted, not minimized; isVisible() also checks the tab.
  useEffect(() => (minimized ? undefined : markVisible(channelId)), [channelId, minimized]);

  const other = channel?.kind === "dm" ? (channel.members.find((m) => m !== me) ?? me) : null;
  const title = channel?.name ?? "Conversation";
  const subtitle = other
    ? PRESENCE_TEXT[presenceOf(other, people.data)]
    : channel
      ? [`${channel.members.length} members`, channel.topic].filter(Boolean).join(" · ")
      : "";

  const onDrop = (e: DragEvent) => {
    e.preventDefault();
    setDragging(false);
    const files = [...e.dataTransfer.files];
    if (files.length) composer.current?.attach(files);
  };

  return (
    <section
      className={clsx(s.conversation, variant === "dock" ? s.dockWindow : s.full, minimized && s.minimized)}
      aria-label={`Chat with ${title}`}
      onDragOver={(e) => {
        if (e.dataTransfer.types.includes("Files")) {
          e.preventDefault();
          setDragging(true);
        }
      }}
      onDragLeave={(e) => e.currentTarget === e.target && setDragging(false)}
      onDrop={onDrop}
    >
      <header className={s.convHeader} onClick={variant === "dock" && minimized ? onMinimize : undefined}>
        {other ? <Avatar name={title} email={other} size={32} presence={presenceOf(other, people.data)} /> : <span className={s.groupIcon} aria-hidden><Users /></span>}
        <div className={s.convTitleWrap}>
          <h2 className={s.convTitle}>
            {title}
            {channel?.muted && <BellOff className={s.mutedIcon} aria-label="Muted" />}
          </h2>
          {!minimized && <p className={s.convSub}>{subtitle}</p>}
        </div>
        <div className={s.convActions} onClick={(e) => e.stopPropagation()}>
          {channel && !minimized && (
            <Menu>
              <MenuTrigger asChild>
                <IconButton label="Conversation options" icon={<MoreHorizontal />} size="sm" />
              </MenuTrigger>
              <MenuContent>
                <MenuItem icon={channel.muted ? <Bell /> : <BellOff />} onSelect={() => void updateChannel(channel.id, { muted: !channel.muted })}>
                  {channel.muted ? "Unmute" : "Mute notifications"}
                </MenuItem>
                {channel.kind === "group" && (
                  <>
                    <MenuItem icon={<Type />} onSelect={() => setDialog("rename")}>Rename or set topic</MenuItem>
                    <MenuItem icon={<UserPlus />} onSelect={() => setDialog("members")}>People in this group</MenuItem>
                    <MenuSeparator />
                    <MenuItem icon={<LogOut />} danger onSelect={() => void leaveChannel(channel.id).then(() => onClose?.())}>Leave group</MenuItem>
                  </>
                )}
              </MenuContent>
            </Menu>
          )}
          {onMinimize && <IconButton label={minimized ? "Expand" : "Minimize"} icon={minimized ? <Maximize2 /> : <Minus />} size="sm" onClick={onMinimize} />}
          {onExpand && !minimized && <IconButton label="Open in full view" icon={<Maximize2 />} size="sm" onClick={onExpand} />}
          {onClose && <IconButton label="Close chat" icon={<X />} size="sm" onClick={onClose} />}
        </div>
      </header>

      {!minimized && (
        <>
          <MessageList
            channel={channel}
            me={me}
            messages={list}
            hasMore={messages.hasNextPage}
            loadingMore={messages.isFetchingNextPage}
            loading={messages.isPending}
            onLoadMore={() => void messages.fetchNextPage()}
            onReply={(m) => {
              setEditing(null);
              setReplyTo(m);
              composer.current?.focus();
            }}
            onEdit={(m) => {
              setReplyTo(null);
              setEditing(m);
              composer.current?.startEdit(m);
            }}
            onDelete={setConfirmDelete}
            nameOf={(e) => nameOf(e, people.data)}
          />
          <p className={s.typing} aria-live="polite">
            {typingText(typing.map((e) => nameOf(e, people.data).split(" ")[0] ?? e))}
          </p>
          <Composer
            ref={composer}
            channelId={channelId}
            me={me}
            placeholder={other ? `Message ${title.split(" ")[0]}` : `Message ${title}`}
            replyTo={replyTo}
            editing={editing}
            onCancel={() => {
              setReplyTo(null);
              setEditing(null);
            }}
            onSent={() => setReplyTo(null)}
            onEditLast={() => {
              const mine = [...list].reverse().find((m) => m.author === me && !m.deleted && m.id > 0 && m.body);
              if (mine) {
                setEditing(mine);
                composer.current?.startEdit(mine);
              }
            }}
            nameOf={(e) => nameOf(e, people.data)}
            autoFocus={variant === "dock"}
          />
          {dragging && <div className={s.dropHint}>Drop to attach</div>}
        </>
      )}

      <Dialog open={confirmDelete !== null} onOpenChange={(o) => !o && setConfirmDelete(null)} title="Delete this message?" description="It is removed for everyone in this conversation, including any attached file.">
        <div className={s.dialogActions}>
          <Button variant="ghost" onClick={() => setConfirmDelete(null)}>Cancel</Button>
          <Button
            variant="danger"
            onClick={() => {
              if (confirmDelete) void deleteMessage(confirmDelete.id);
              setConfirmDelete(null);
            }}
          >
            Delete
          </Button>
        </div>
      </Dialog>
      {channel && dialog === "rename" && <RenameDialog channel={channel} onClose={() => setDialog(null)} />}
      {channel && dialog === "members" && <MembersDialog channel={channel} me={me} onClose={() => setDialog(null)} />}
    </section>
  );
}

function MessageList(props: {
  channel: ChatChannel | undefined;
  me: string;
  messages: UiMessage[];
  hasMore: boolean;
  loadingMore: boolean;
  loading: boolean;
  onLoadMore: () => void;
  onReply: (m: UiMessage) => void;
  onEdit: (m: UiMessage) => void;
  onDelete: (m: UiMessage) => void;
  nameOf: (email: string) => string;
}) {
  const { channel, me, messages } = props;
  const box = useRef<HTMLDivElement>(null);
  const atBottom = useRef(true);
  const prevHeight = useRef(0);
  const prevFirst = useRef<number | undefined>(undefined);
  // The "New" marker stays where it was when the conversation opened, not where reading has got to since.
  const [lastReadAtOpen] = useState(() => channel?.readUpTo[me] ?? Number.MAX_SAFE_INTEGER);
  const rows = useMemo(() => buildRows(messages, { me, lastRead: lastReadAtOpen }), [messages, me, lastReadAtOpen]);
  const byId = useMemo(() => new Map(messages.map((m) => [m.id, m])), [messages]);
  const lastServerId = [...messages].reverse().find((m) => m.id > 0)?.id ?? 0;

  // Keep the reader's place: stick to the bottom for new messages, hold position when older ones load above.
  useLayoutEffect(() => {
    const el = box.current;
    if (!el) return;
    const first = messages[0]?.id;
    if (prevFirst.current !== undefined && first !== prevFirst.current && prevHeight.current) el.scrollTop += el.scrollHeight - prevHeight.current;
    else if (atBottom.current) el.scrollTop = el.scrollHeight;
    prevFirst.current = first;
    prevHeight.current = el.scrollHeight;
  }, [messages]);

  // Read receipts: only when the newest message is actually in view.
  useEffect(() => {
    const read = () => {
      if (atBottom.current && document.visibilityState === "visible" && channel) markRead(channel.id, lastServerId);
    };
    read();
    document.addEventListener("visibilitychange", read);
    return () => document.removeEventListener("visibilitychange", read);
  }, [lastServerId, channel]);

  const onScroll = () => {
    const el = box.current;
    if (!el) return;
    atBottom.current = el.scrollHeight - el.scrollTop - el.clientHeight < 48;
    if (atBottom.current && channel) markRead(channel.id, lastServerId);
    if (el.scrollTop < 120 && props.hasMore && !props.loadingMore) {
      prevHeight.current = el.scrollHeight;
      props.onLoadMore();
    }
  };

  const other = channel?.kind === "dm" ? channel.members.find((m) => m !== me) : undefined;
  const seenId = other ? (channel?.readUpTo[other] ?? 0) : 0;
  const lastMine = [...messages].reverse().find((m) => m.author === me && m.id > 0);

  return (
    <div ref={box} className={s.messages} onScroll={onScroll} role="log" aria-label="Messages" tabIndex={0}>
      {props.loadingMore && <p className={s.loadingOlder}>Loading earlier messages</p>}
      {!props.loading && messages.length === 0 && (
        <div className={s.convEmpty}>
          <p className={s.convEmptyTitle}>{channel?.kind === "group" ? `This is the start of ${channel.name}` : `This is the start of your conversation with ${channel?.name ?? "them"}`}</p>
          <p>Messages are visible to everyone in this conversation.</p>
        </div>
      )}
      {rows.map((r) => {
        if (r.kind === "day") return <div key={r.key} className={s.day} role="separator"><span>{r.label}</span></div>;
        if (r.kind === "unread") return <div key={r.key} className={s.newMarker} role="separator"><span>New</span></div>;
        const m = r.message;
        const mine = m.author === me;
        const name = mine ? "You" : props.nameOf(m.author);
        const quoted = m.replyTo !== null ? byId.get(m.replyTo) : undefined;
        return (
          <Fragment key={r.key}>
            <article className={clsx(s.msg, r.first && s.msgFirst, m.pending && s.pending, m.failed && s.failed)} aria-label={`${name}, ${timeLabel(new Date(m.createdAt))}`}>
              <div className={s.msgGutter}>
                {r.first ? <Avatar name={props.nameOf(m.author)} email={m.author} size={32} /> : <time className={s.hoverTime} dateTime={m.createdAt}>{timeLabel(new Date(m.createdAt))}</time>}
              </div>
              <div className={s.msgMain}>
                {r.first && (
                  <div className={s.msgHead}>
                    <span className={s.msgAuthor}>{name}</span>
                    <time className={s.msgTime} dateTime={m.createdAt} title={new Date(m.createdAt).toLocaleString()}>{timeLabel(new Date(m.createdAt))}</time>
                  </div>
                )}
                {m.replyTo !== null && (
                  <div className={s.quote}>
                    <CornerUpLeft aria-hidden />
                    <span className={s.quoteAuthor}>{quoted ? (quoted.author === me ? "You" : props.nameOf(quoted.author)) : "Earlier message"}</span>
                    <span className={s.quoteText}>{quoted ? (quoted.deleted ? "Deleted message" : quoted.body || quoted.file?.name || "") : ""}</span>
                  </div>
                )}
                {m.deleted ? (
                  <p className={s.deleted}>This message was deleted</p>
                ) : (
                  <>
                    {m.body && (
                      <p className={s.body}>
                        {linkify(m.body).map((seg, i) =>
                          seg.href ? (
                            <a key={i} href={seg.href} target="_blank" rel="noopener noreferrer nofollow">{seg.text}</a>
                          ) : (
                            <Fragment key={i}>{seg.text}</Fragment>
                          ),
                        )}
                        {m.editedAt && <span className={s.edited}> (edited)</span>}
                      </p>
                    )}
                    {m.file && <FileCard file={m.file} />}
                  </>
                )}
                {m.pending && <p className={s.status}>Sending</p>}
                {m.failed && (
                  <p className={clsx(s.status, s.statusError)}>
                    Not sent.{" "}
                    <button type="button" className={s.linkButton} onClick={() => m.clientId && channel && removeFailed(channel.id, m.clientId)}>Discard</button>
                  </p>
                )}
                {other && lastMine && m.id === lastMine.id && seenId >= m.id && <p className={s.seen}>Seen</p>}
              </div>
              {!m.deleted && !m.pending && !m.failed && (
                <div className={s.msgTools} role="toolbar" aria-label="Message actions">
                  <IconButton label="Reply" icon={<CornerUpLeft />} size="sm" onClick={() => props.onReply(m)} />
                  {mine && m.body && <IconButton label="Edit" icon={<Pencil />} size="sm" onClick={() => props.onEdit(m)} />}
                  {mine && <IconButton label="Delete" icon={<Trash2 />} size="sm" onClick={() => props.onDelete(m)} />}
                </div>
              )}
            </article>
          </Fragment>
        );
      })}
    </div>
  );
}

function FileCard({ file }: { file: ChatFile }) {
  const image = /^image\/(png|jpeg|gif|webp)$/.test(file.mime);
  if (image) {
    return (
      <a className={s.imageLink} href={fileUrl(file.id)} target="_blank" rel="noopener noreferrer">
        <img className={s.image} src={fileUrl(file.id)} alt={file.name} loading="lazy" />
      </a>
    );
  }
  return (
    <a className={s.fileCard} href={fileUrl(file.id, true)} download={file.name}>
      <FileText aria-hidden className={s.fileIcon} />
      <span className={s.fileMeta}>
        <span className={s.fileName}>{file.name}</span>
        <span className={s.fileSize}>{formatSize(file.size)}</span>
      </span>
      <Download aria-hidden className={s.fileDownload} />
    </a>
  );
}

interface ComposerHandle {
  focus(): void;
  attach(files: File[]): void;
  startEdit(m: UiMessage): void;
}

interface Upload {
  key: string;
  name: string;
  progress: number;
  error?: string;
}

function Composer({ ref, channelId, me, placeholder, replyTo, editing, onCancel, onSent, onEditLast, nameOf: nameFor, autoFocus }: {
  ref: React.Ref<ComposerHandle>;
  channelId: string;
  me: string;
  placeholder: string;
  replyTo: UiMessage | null;
  editing: UiMessage | null;
  onCancel: () => void;
  onSent: () => void;
  onEditLast: () => void;
  nameOf: (email: string) => string;
  autoFocus: boolean;
}) {
  const [text, setText] = useState("");
  const [uploads, setUploads] = useState<Upload[]>([]);
  const area = useRef<HTMLTextAreaElement>(null);
  const fileInput = useRef<HTMLInputElement>(null);
  const lastTyping = useRef(0);
  const send = useSendMessage(channelId, me);

  useLayoutEffect(() => {
    const el = area.current;
    if (!el) return;
    el.style.height = "auto";
    el.style.height = `${Math.min(el.scrollHeight, 160)}px`;
  }, [text]);

  const attach = (files: File[]) => {
    for (const file of files) {
      const key = `${file.name}-${Math.random().toString(36).slice(2)}`;
      if (file.size > CHAT_FILE_MAX_BYTES) {
        setUploads((u) => [...u, { key, name: file.name, progress: 0, error: "Larger than 10 MB" }]);
        continue;
      }
      setUploads((u) => [...u, { key, name: file.name, progress: 0 }]);
      uploadFile(channelId, file, (p) => setUploads((u) => u.map((x) => (x.key === key ? { ...x, progress: p } : x))))
        .then((f) => {
          setUploads((u) => u.filter((x) => x.key !== key));
          send.mutate({ body: "", file: f, clientId: crypto.randomUUID() });
        })
        .catch((err: unknown) => setUploads((u) => u.map((x) => (x.key === key ? { ...x, error: err instanceof ApiFailure ? err.message : "Upload failed" } : x))));
    }
  };

  // React 19: ref is a regular prop on function components.
  useImperativeHandle(ref, () => ({
    focus: () => area.current?.focus(),
    attach,
    startEdit: (m) => {
      setText(m.body);
      requestAnimationFrame(() => {
        area.current?.focus();
        area.current?.setSelectionRange(m.body.length, m.body.length);
      });
    },
  }));

  const submit = () => {
    const body = text.trim();
    if (editing) {
      if (body && body !== editing.body) void editMessage(editing.id, body);
      setText("");
      onCancel();
      return;
    }
    if (!body) return;
    send.mutate({ body, replyTo: replyTo?.id, clientId: crypto.randomUUID() });
    setText("");
    onSent();
  };

  const onKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey && !e.nativeEvent.isComposing) {
      e.preventDefault();
      submit();
    } else if (e.key === "Escape" && (editing || replyTo)) {
      e.preventDefault();
      e.stopPropagation();
      setText(editing ? "" : text);
      onCancel();
    } else if (e.key === "ArrowUp" && !text && !editing) {
      e.preventDefault();
      onEditLast();
    }
  };

  const onPaste = (e: ClipboardEvent) => {
    const files = [...e.clipboardData.files];
    if (files.length) {
      e.preventDefault();
      attach(files);
    }
  };

  return (
    <div className={s.composer}>
      {(replyTo || editing) && (
        <div className={s.context}>
          {editing ? <Pencil aria-hidden /> : <CornerUpLeft aria-hidden />}
          <span className={s.contextText}>
            {editing ? "Editing message" : `Replying to ${replyTo?.author === me ? "yourself" : nameFor(replyTo?.author ?? "")}`}
            {replyTo && <span className={s.contextQuote}> {replyTo.body || replyTo.file?.name}</span>}
          </span>
          <IconButton label="Cancel" shortcut="Esc" icon={<X />} size="sm" onClick={() => { setText(editing ? "" : text); onCancel(); }} />
        </div>
      )}
      {uploads.map((u) => (
        <div key={u.key} className={s.upload}>
          <Paperclip aria-hidden />
          <span className={s.uploadName}>{u.name}</span>
          {u.error ? <span className={s.statusError}>{u.error}</span> : <progress className={s.progress} value={u.progress} max={1} aria-label={`Uploading ${u.name}`} />}
          {u.error && <IconButton label="Dismiss" icon={<X />} size="sm" onClick={() => setUploads((x) => x.filter((y) => y.key !== u.key))} />}
        </div>
      ))}
      <div className={s.inputRow}>
        <IconButton label="Attach a file" icon={<Paperclip />} size="sm" onClick={() => fileInput.current?.click()} disabled={Boolean(editing)} />
        <input
          ref={fileInput}
          type="file"
          multiple
          hidden
          onChange={(e) => {
            attach([...(e.target.files ?? [])]);
            e.target.value = "";
          }}
        />
        <textarea
          ref={area}
          className={s.input}
          rows={1}
          value={text}
          placeholder={placeholder}
          aria-label={placeholder}
          autoFocus={autoFocus}
          onChange={(e) => {
            setText(e.target.value);
            const now = Date.now();
            if (e.target.value && now - lastTyping.current > 3000) {
              lastTyping.current = now;
              socketSend({ type: "typing", channelId });
            }
          }}
          onKeyDown={onKeyDown}
          onPaste={onPaste}
        />
        <IconButton label={editing ? "Save" : "Send"} shortcut="Enter" icon={<SendHorizontal />} size="sm" className={s.sendButton} onClick={submit} disabled={!text.trim()} />
      </div>
    </div>
  );
}
